import React from 'react';
import { Link, LinkProps } from 'react-router-dom';

import { Avatar, LazyImage, Clamp } from '@crehana/ui';
import PastLiveCard from '@crehana/web/dist/components/Cards/PastLiveCard';

import { useMe } from 'Jsx/ContextApis/AuthContext';
import OnLiveTag from 'Jsx/CrehanaLive/components/OnLiveTag';
import type { LiveCategory } from 'Jsx/CrehanaLive/types';

import { CREHANA_LIVE_AMPLITUDE_PAGE_NAME } from '../constants';
import type { LiveNode } from '../graphql/types/LiveNode';
import type { Presenter } from '../graphql/types/Presenter';

interface CardProps extends LiveNode {
  linkTo: LinkProps['to'];
  speakers: Presenter[];
  speakersNames: string;
  isMultiSpeaker: boolean;
  onClickCategory?: (category?: LiveCategory) => void;
}

const IMAGE_HEIGHT = 153;

const NormalCard: React.FC<CardProps> = React.memo(({ linkTo, ...live }) => {
  const { me } = useMe();

  return (
    <Link to={linkTo} className="relative inline-block w-full flex-shrink-0">
      <PastLiveCard
        isPremium={!!me?.isPremium}
        disableRedirections
        source={CREHANA_LIVE_AMPLITUDE_PAGE_NAME}
        sourceDetail="Live List"
        {...live}
      />
    </Link>
  );
});

const KidsCard: React.FC<CardProps> = ({
  linkTo,
  speakers,
  speakersNames,
  isMultiSpeaker,
  isLive,
  title,
  coverCard,
}) => {
  const radius = 10;

  return (
    <article
      className="flex flex-col w-full h-full bg-white"
      style={{ borderRadius: radius }}
    >
      <Link
        to={linkTo}
        style={{ height: IMAGE_HEIGHT }}
        className="relative inline-block w-full"
      >
        <LazyImage
          className="w-full h-full"
          imgClassName="object-cover"
          imgStyle={{
            borderTopLeftRadius: radius,
            borderTopRightRadius: radius,
          }}
          src={coverCard || ''}
          alt={title}
          imgxParams={{
            w: 270,
            h: IMAGE_HEIGHT,
          }}
          isDark
        />
        {isLive && (
          <div className="absolute" style={{ top: 16, left: 16 }}>
            <OnLiveTag />
          </div>
        )}
      </Link>

      <div className="flex flex-col justify-between h-full p-16 sm:p-8 md:p-16">
        <div>
          <Link
            className="inline-block w-full"
            to={linkTo}
            style={{ minHeight: 58 }}
          >
            <Clamp
              as="h4"
              className="font-subtitle3 text-black hover:text-secondary-light"
              clamp={3}
            >
              {title}
            </Clamp>
          </Link>
          <div className="flex items-start">
            <div
              className="relative h-16 mr-8"
              style={{
                width: 16 + (isMultiSpeaker ? speakers.length * 8 : 0),
              }}
            >
              {speakers.map((speaker, i) => (
                <Avatar
                  key={speaker.originalId!}
                  src={speaker.thumbnailUrl}
                  size={16}
                  className="absolute"
                  style={{ left: 8 * i }}
                />
              ))}
            </div>
            <span
              className={`${
                isMultiSpeaker ? 'font-legals' : 'font-body4'
              } text-gray-dark`}
            >
              {speakersNames}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

const B2BCard: React.FC<CardProps> = React.memo(
  ({
    linkTo,
    speakers,
    speakersNames,
    isMultiSpeaker,
    isLive,
    title,
    coverCard,
  }) => {
    return (
      <article className="flex flex-col w-full h-full bg-base-lighter">
        <Link
          to={linkTo}
          style={{ height: IMAGE_HEIGHT }}
          className="relative inline-block w-full"
        >
          <LazyImage
            className="w-full h-full"
            imgClassName="object-cover"
            src={coverCard || ''}
            alt={title}
            imgxParams={{
              w: 270,
              h: IMAGE_HEIGHT,
            }}
            isDark
          />
          {isLive && (
            <div className="absolute" style={{ top: 16, left: 16 }}>
              <OnLiveTag />
            </div>
          )}
        </Link>

        <div className="flex flex-col justify-between h-full p-16 sm:p-8 md:p-16">
          <div>
            <Link className="inline-block w-full" to={linkTo}>
              <Clamp
                as="h4"
                className="font-subtitle4 text-white hover:text-secondary-light"
                clamp={3}
              >
                {title}
              </Clamp>
            </Link>
            <div className="flex items-start">
              <div
                className="relative h-16 mr-8"
                style={{
                  width: 16 + (isMultiSpeaker ? speakers.length * 8 : 0),
                }}
              >
                {speakers.map((speaker, i) => (
                  <Avatar
                    key={speaker.originalId!}
                    src={speaker.thumbnailUrl}
                    size={16}
                    className="absolute"
                    style={{ left: 8 * i }}
                  />
                ))}
              </div>
              <span
                className={`${
                  isMultiSpeaker ? 'font-legals' : 'font-body4'
                } text-gray-main`}
              >
                {speakersNames}
              </span>
            </div>
          </div>
        </div>
      </article>
    );
  },
);

interface LiveCardProps extends LiveNode {
  type: CardType;
  onClickCategory?: (category?: LiveCategory) => void;
}
export type CardType = 'normal' | 'b2b' | 'kids';

const LiveCard: React.FC<LiveCardProps> = ({
  type,
  onClickCategory,
  ...live
}) => {
  const { liveDetailUrl, presenter, presenters } = live;

  const linkTo = {
    pathname: liveDetailUrl!,
    state: {
      liveDetail: live,
    },
  };

  const speakers: Presenter[] = React.useMemo(() => {
    if (presenters?.edges.length) {
      return presenters.edges.map(e => e!.node!);
    }
    if (presenter) {
      return [presenter];
    }
    return [];
  }, [presenter, presenters]);

  const speakersNames = React.useMemo(() => {
    if (speakers.length > 2) {
      return `${speakers[0]!.fullName} + ${speakers.length - 1} Speakers`;
    }

    const names = speakers.map(({ fullName }) => fullName);

    return names.join(' + ');
  }, [speakers]);

  const isMultiSpeaker = speakers.length > 1;

  switch (type) {
    case 'normal':
      return (
        <NormalCard
          {...live}
          linkTo={linkTo}
          speakers={speakers}
          speakersNames={speakersNames}
          isMultiSpeaker={isMultiSpeaker}
          onClickCategory={onClickCategory}
          presenters={presenters}
        />
      );

    case 'b2b':
      return (
        <B2BCard
          linkTo={linkTo}
          speakers={speakers}
          speakersNames={speakersNames}
          isMultiSpeaker={isMultiSpeaker}
          {...live}
        />
      );

    case 'kids':
      return (
        <KidsCard
          linkTo={linkTo}
          speakers={speakers}
          speakersNames={speakersNames}
          isMultiSpeaker={isMultiSpeaker}
          {...live}
        />
      );

    default:
      return (
        <NormalCard
          {...live}
          linkTo={linkTo}
          speakers={speakers}
          speakersNames={speakersNames}
          isMultiSpeaker={isMultiSpeaker}
          onClickCategory={onClickCategory}
          presenters={presenters}
        />
      );
  }
};

export default LiveCard;
